import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import { useGetMarketplaceUrlQuery, useGetUnreadCountQuery } from "../../api";
import { useGetClientCabinetUrlQuery } from "../../api/crossAuth";
import { useTheme } from "../../hooks/useTheme";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import NotificationsDropdown from "../NotificationsDropdown";
import LanguageSelector from "../LanguageSelector";

interface HeaderProps {
    onMenuToggle?: () => void;
    isDesktopSidebarCollapsed?: boolean;
    onDesktopSidebarToggle?: () => void;
}

const Header: React.FC<HeaderProps> = ({
    onMenuToggle,
    isDesktopSidebarCollapsed = false,
    onDesktopSidebarToggle,
}) => {
    const { user } = useSelector((state: any) => state.auth);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const { t } = useTranslation("common");
    const { data: marketplaceUrlData, refetch: refetchMarketplaceUrl } =
        useGetMarketplaceUrlQuery();
    const { data: clientCabinetUrlData, refetch: refetchClientCabinetUrl } =
        useGetClientCabinetUrlQuery();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const notificationsRef = useRef<HTMLDivElement>(null);

    // Получение количества непрочитанных уведомлений
    const { data: unreadCountData } = useGetUnreadCountQuery();

    // Закрываем dropdown при клике вне его
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
            if (
                notificationsRef.current &&
                !notificationsRef.current.contains(event.target as Node)
            ) {
                setIsNotificationsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        // Удаляем токен из куки
        Cookies.remove("token");

        // Обновляем Redux store
        dispatch(logout());

        // Перенаправляем на главную страницу
        navigate("/");
    };

    const handleProfileClick = () => {
        navigate("/dashboard/profile");
        setIsDropdownOpen(false);
    };

    const handleGoToMarketplace = async () => {
        try {
            await refetchMarketplaceUrl();
            if (marketplaceUrlData?.url) {
                window.open(marketplaceUrlData.url, "_blank");
            } else {
                alert(t("common.marketplace_error"));
            }
        } catch (error) {
            console.error("Failed to get marketplace URL:", error);
            alert(t("common.marketplace_error"));
        }
    };

    const handleGoToClientCabinet = async () => {
        try {
            await refetchClientCabinetUrl();
            if (clientCabinetUrlData?.url) {
                window.location.href = clientCabinetUrlData.url;
            } else {
                alert(t("common.client_cabinet_error"));
            }
        } catch (error) {
            console.error("Failed to get client cabinet URL:", error);
            alert(t("common.client_cabinet_error"));
        }
    };

    const handleNotificationsToggle = () => {
        setIsNotificationsOpen(!isNotificationsOpen);
    };

    return (
        <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 px-4 lg:px-6 py-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center">
                    {/* Мобильная кнопка меню */}
                    {onMenuToggle && (
                        <button
                            onClick={onMenuToggle}
                            className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-3"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            </svg>
                        </button>
                    )}
                    {/* Десктоп: свернуть/развернуть сайдбар */}
                    {onDesktopSidebarToggle && (
                        <button
                            onClick={onDesktopSidebarToggle}
                            className="hidden lg:flex p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-3"
                            title={
                                isDesktopSidebarCollapsed
                                    ? "Показать меню"
                                    : "Скрыть меню"
                            }
                        >
                            {isDesktopSidebarCollapsed ? (
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                </svg>
                            ) : (
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                    />
                                </svg>
                            )}
                        </button>
                    )}
                    <h1 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-200">
                        Alny
                    </h1>
                </div>

                <div className="flex items-center space-x-2 lg:space-x-4">
                    {/* Иконка уведомлений */}
                    <div className="relative" ref={notificationsRef}>
                        <button
                            onClick={handleNotificationsToggle}
                            className="relative flex items-center justify-center p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            title={t("navigation.notifications")}
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 17h5l-5 5v-5zM4.5 19.5c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5M9 15c-2.485 0-4.5-2.015-4.5-4.5S6.515 6 9 6s4.5 2.015 4.5 4.5S11.485 15 9 15z"
                                />
                            </svg>
                            {/* Счетчик непрочитанных уведомлений */}
                            {unreadCountData?.count &&
                                unreadCountData.count > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                                        {unreadCountData.count > 99
                                            ? "99+"
                                            : unreadCountData.count}
                                    </span>
                                )}
                        </button>

                        {/* Выпадающий список уведомлений */}
                        <NotificationsDropdown
                            isOpen={isNotificationsOpen}
                            onClose={() => setIsNotificationsOpen(false)}
                        />
                    </div>

                    {/* Переключатель темы */}
                    <button
                        onClick={toggleTheme}
                        className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                        title={
                            theme === "dark" ? "Светлая тема" : "Темная тема"
                        }
                    >
                        {theme === "dark" ? (
                            // Иконка солнца (светлая тема)
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                                />
                            </svg>
                        ) : (
                            // Иконка луны (темная тема)
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                                />
                            </svg>
                        )}
                    </button>

                    {/* Выпадающий список пользователя */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                        >
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-blue-600 dark:bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                    {user?.name
                                        ? user.name.charAt(0).toUpperCase()
                                        : "U"}
                                </div>
                                <div className="hidden sm:block text-left">
                                    <div className="font-medium">
                                        {user?.name && user?.surname
                                            ? `${user.name} ${user.surname}`
                                            : user?.name || t("user")}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {user?.roles?.[0] || "user"}
                                    </div>
                                </div>
                            </div>
                            <svg
                                className={`w-4 h-4 transition-transform ${
                                    isDropdownOpen ? "rotate-180" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>

                        {/* Выпадающее меню */}
                        {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={handleProfileClick}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4 mr-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                        />
                                    </svg>
                                    {t("navigation.profile")}
                                </button>

                                <button
                                    onClick={handleGoToMarketplace}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4 mr-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                    </svg>
                                    {t("navigation.marketplace")}
                                </button>

                                <button
                                    onClick={handleGoToClientCabinet}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4 mr-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                        />
                                    </svg>
                                    {t("navigation.client_cabinet")}
                                </button>

                                <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

                                <button
                                    onClick={handleLogout}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4 mr-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                        />
                                    </svg>
                                    {t("navigation.logout")}
                                </button>

                                <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

                                {/* Переключатель языка */}
                                <div className="px-4 py-2">
                                    <LanguageSelector />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
